import { faCheck, faMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode } from 'react';

import { cn } from '../lib/utils';

export type CheckboxColor = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';

const colorClasses: Record<CheckboxColor, { box: string; ring: string }> = {
  primary: {
    box: 'border-green-600 bg-green-600 dark:border-green-500 dark:bg-green-500',
    ring: 'has-[:focus-visible]:ring-green-500'
  },
  secondary: {
    box: 'border-blue-600 bg-blue-600 dark:border-blue-500 dark:bg-blue-500',
    ring: 'has-[:focus-visible]:ring-blue-500'
  },
  destructive: {
    box: 'border-uniform-red-600 bg-uniform-red-600 dark:border-uniform-red-500 dark:bg-uniform-red-500',
    ring: 'has-[:focus-visible]:ring-uniform-red-500'
  },
  warning: {
    box: 'border-yellow-500 bg-yellow-500 dark:border-yellow-400 dark:bg-yellow-400',
    ring: 'has-[:focus-visible]:ring-yellow-500'
  },
  neutral: {
    box: 'border-uniform-gray-600 bg-uniform-gray-600 dark:border-uniform-gray-500 dark:bg-uniform-gray-500',
    ring: 'has-[:focus-visible]:ring-uniform-gray-500'
  }
};

export interface CheckboxProps {
  checked: boolean;
  className?: string;
  color?: CheckboxColor;
  disabled?: boolean;
  helpText?: string;
  /** Keeps the label for screen readers but removes it from the layout. */
  hideLabel?: boolean;
  id?: string;
  indeterminate?: boolean;
  label?: string | ReactNode;
  onChange: (checked: boolean) => void;
  required?: boolean;
}

function Checkbox({
  checked,
  className,
  color = 'primary',
  disabled = false,
  helpText,
  hideLabel = false,
  id,
  indeterminate = false,
  label,
  onChange,
  required
}: CheckboxProps) {
  const { box, ring } = colorClasses[color];
  // With nothing rendered beside it the box is the whole control, so it centers
  // in its row instead of hanging from the first line of label text.
  const standalone = (!label || hideLabel) && !helpText;
  return (
    <div className={cn('flex gap-2', standalone ? 'items-center' : 'items-start', className)}>
      {/*
       * Wrapper is the visual box. The native input sits on top (opacity-0, z-10)
       * so it receives all pointer events. has-[:focus-visible] shows the ring on
       * the wrapper when the input inside is keyboard-focused.
       */}
      <div
        className={cn(
          'relative flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
          'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-offset-1',
          !standalone && 'mt-0.5',
          ring,
          checked || indeterminate
            ? box
            : 'border-uniform-gray-300 bg-white dark:border-uniform-gray-600 dark:bg-uniform-gray-800',
          disabled && 'opacity-50'
        )}>
        <div className="pointer-events-none z-0 text-[9px]">
          {indeterminate ? (
            <FontAwesomeIcon
              icon={faMinus}
              className={color === 'warning' ? 'text-yellow-950' : 'text-white'}
            />
          ) : checked ? (
            <FontAwesomeIcon
              icon={faCheck}
              className={color === 'warning' ? 'text-yellow-950' : 'text-white'}
            />
          ) : null}
        </div>
        {/* Native input: invisible but covers the box and handles all events */}
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          required={required}
          onChange={(e) => onChange(e.target.checked)}
          ref={(el) => {
            if (el) el.indeterminate = indeterminate;
          }}
          className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
      </div>
      {(label || helpText) && (
        <div className={cn('flex flex-col gap-0.5', standalone && 'contents')}>
          {label && (
            <label
              htmlFor={id}
              className={cn(
                'text-sm leading-relaxed text-uniform-gray-900 dark:text-uniform-gray-100',
                !disabled && 'cursor-pointer',
                hideLabel && 'sr-only'
              )}>
              {label}
            </label>
          )}
          {helpText && (
            <p className="text-xs text-uniform-gray-500 dark:text-uniform-gray-400">{helpText}</p>
          )}
        </div>
      )}
    </div>
  );
}

export { Checkbox };
